import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminGet, adminPost, adminPut } from './adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowRight, Share2, FileText } from 'lucide-react';

const GREEN = '#0B6B55';
const TYPE_LABEL = { START: 'START', STORAGE: 'STORAGE', TRANSIT: 'TRANSIT', LAST_MILE: 'LAST MILE', END: 'END' };

function StageChip({ stage, selected, onClick }) {
  const dashed = stage.stage_type === 'END';
  return (
    <button data-testid={`stage-chip-${stage.position}`} onClick={onClick}
      className="text-left rounded-md border px-3 py-2 min-w-[130px] shrink-0 transition-colors"
      style={selected ? { background: '#E7F2EE', borderColor: GREEN }
        : { background: dashed ? 'white' : '#FAF9F6', borderColor: '#D9D6CD', borderStyle: dashed ? 'dashed' : 'solid' }}>
      <p className="text-[10px] font-bold tracking-wide" style={{ color: '#6b7069' }}>
        {TYPE_LABEL[stage.stage_type]}{!stage.required && ' · OPTIONAL'}</p>
      <p className="text-sm font-bold" style={{ color: '#1B1D1C' }}>{stage.name}</p>
      <p className="text-xs" style={{ color: '#9a9e96' }}>{(stage.allowed_device_types || []).join(' / ') || '—'}</p>
    </button>
  );
}

function StageSettings({ stage, onChange, onRemove }) {
  const f = (label, node) => (
    <div><label className="text-sm block mb-1" style={{ color: '#4b504a' }}>{label}</label>{node}</div>
  );
  const sel = (key, options, testid) => (
    <select data-testid={testid} value={stage[key] ?? ''} onChange={e => onChange({ ...stage, [key]: e.target.value })}
      className="w-full h-9 px-3 rounded-md border bg-white text-sm" style={{ borderColor: '#D9D6CD' }}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  return (
    <div className="rounded-lg border bg-white p-6 mt-6" style={{ borderColor: '#E5E3DC' }} data-testid="stage-settings-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg" style={{ color: '#1B1D1C' }}>Stage settings · {stage.name}</h3>
        <Button size="sm" variant="outline" data-testid="remove-stage-btn" onClick={onRemove}
          className="bg-white border-[#D9D6CD]" style={{ color: '#8C2B2B' }}>Remove stage</Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {f('Stage name', <Input data-testid="stage-name-input" value={stage.name}
          onChange={e => onChange({ ...stage, name: e.target.value })} className="bg-white border-[#D9D6CD]" />)}
        {f('Stage type', sel('stage_type', [['STORAGE', 'Storage (fixed location)'], ['TRANSIT', 'Transit'], ['LAST_MILE', 'Last mile'], ['START', 'Start'], ['END', 'End']], 'stage-type-select'))}
        {f('Custodian', sel('custodian_kind', [['SAME_CUSTOMER', 'Same customer'], ['PARTNER', 'Another company (partner)']], 'stage-custodian-select'))}
        {f('Allowed device types', <Input data-testid="stage-devices-input"
          value={(stage.allowed_device_types || []).join(', ')}
          onChange={e => onChange({ ...stage, allowed_device_types: e.target.value.split(',').map(x => x.trim().toUpperCase()).filter(Boolean) })}
          placeholder="HUB, PRO" className="bg-white border-[#D9D6CD]" />)}
        {f('Temperature limits (°C)', <div className="flex gap-2 items-center">
          <Input data-testid="stage-tmin-input" type="number" value={stage.temp_min_c ?? ''} placeholder="min"
            onChange={e => onChange({ ...stage, temp_min_c: e.target.value === '' ? null : Number(e.target.value) })} className="bg-white border-[#D9D6CD]" />
          <span style={{ color: '#9a9e96' }}>–</span>
          <Input data-testid="stage-tmax-input" type="number" value={stage.temp_max_c ?? ''} placeholder="max"
            onChange={e => onChange({ ...stage, temp_max_c: e.target.value === '' ? null : Number(e.target.value) })} className="bg-white border-[#D9D6CD]" />
        </div>)}
        {f('Max time at this stage (minutes)', <Input data-testid="stage-maxmin-input" type="number"
          value={stage.max_minutes ?? ''} placeholder="[minutes]"
          onChange={e => onChange({ ...stage, max_minutes: e.target.value === '' ? null : Number(e.target.value) })} className="bg-white border-[#D9D6CD]" />)}
        {f('If no Track0tic device here', sel('no_device_policy', [['ALLOW_UNMONITORED', 'Allow, mark as unmonitored'], ['BLOCK_HANDOFF', 'Block the handoff']], 'stage-policy-select'))}
        {f('Stage required?', sel('required', [[true, 'Required'], [false, 'Optional']], 'stage-required-select'))}
      </div>
    </div>
  );
}

export default function AdminJourneyTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [selected, setSelected] = useState(null); // {templateId, stageIdx}
  const [dirty, setDirty] = useState(null); // editable copy of a tenant template
  const navigate = useNavigate();

  const load = useCallback(() => {
    const qs = tenantId ? `?tenant_id=${tenantId}` : '';
    adminGet(`/journey-templates${qs}`).then(setTemplates).catch(e => toast.error(e.message));
  }, [tenantId]);
  useEffect(() => { load(); setSelected(null); setDirty(null); }, [load]);
  useEffect(() => { adminGet('/customers-lite').then(setCustomers).catch(() => {}); }, []);

  const copyTemplate = async (t) => {
    if (!tenantId) { toast.error('Pick a customer first to copy a default for it'); return; }
    const name = window.prompt('Name for the customer copy:', `${t.name} (custom)`);
    if (!name) return;
    try {
      await adminPost('/journey-templates', { tenant_id: tenantId, source_template_id: t.id, name });
      toast.success('Template copied — now editable for this customer');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const saveDirty = async () => {
    try {
      await adminPut(`/journey-templates/${dirty.id}`, {
        name: dirty.name, identifier_type: dirty.identifier_type,
        max_total_minutes: dirty.max_total_minutes, active: dirty.active,
        stages: dirty.stages.map((s, i) => ({ ...s, position: i + 1 })),
      });
      toast.success('Template saved');
      setDirty(null); setSelected(null); load();
    } catch (e) { toast.error(e.message); }
  };

  const renderTemplate = (t) => {
    const editable = !!t.tenant_id;
    const isDirty = dirty && dirty.id === t.id;
    const view = isDirty ? dirty : t;
    return (
      <div key={t.id} className="rounded-lg border bg-white p-5 mb-6" style={{ borderColor: '#E5E3DC' }}
        data-testid={`template-card-${t.id}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg" style={{ color: '#1B1D1C' }}>{view.name}</h2>
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: '#9a9e96' }}>
              {t.tenant_id ? 'Customer template' : `Default for ${t.vertical}`}</span>
            {!editable && <Button size="sm" variant="outline" data-testid={`copy-template-${t.id}`}
              onClick={() => copyTemplate(t)} className="bg-white border-[#D9D6CD]">Copy for customer</Button>}
            {editable && (
              <>
                <Button size="sm" variant="outline" data-testid={`sharing-btn-${t.id}`}
                  onClick={() => navigate(`/admin/customers/${t.tenant_id}/sharing?template=${t.id}`)}
                  className="bg-white border-[#D9D6CD]"><Share2 size={14} className="mr-1" />Ownership &amp; sharing</Button>
                <Button size="sm" variant="outline" data-testid={`content-btn-${t.id}`}
                  onClick={() => navigate(`/admin/customers/${t.tenant_id}/passport-content`)}
                  className="bg-white border-[#D9D6CD]"><FileText size={14} className="mr-1" />Passport content</Button>
                {isDirty && <Button size="sm" data-testid="save-template-btn" onClick={saveDirty}
                  className="text-white" style={{ background: GREEN }}>Save template</Button>}
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 pb-2">
          {view.stages.map((s, i) => (
            <div key={s.id || i} className="flex items-center gap-2">
              <StageChip stage={s} selected={selected?.templateId === t.id && selected?.stageIdx === i}
                onClick={() => {
                  if (!editable) { setSelected(null); return; }
                  if (!isDirty) setDirty(JSON.parse(JSON.stringify(t)));
                  setSelected({ templateId: t.id, stageIdx: i });
                }} />
              {i < view.stages.length - 1 && <ArrowRight size={14} style={{ color: '#9a9e96' }} className="shrink-0" />}
            </div>
          ))}
          {editable && (
            <button data-testid={`add-stage-${t.id}`} className="rounded-md border border-dashed px-3 py-2 text-sm shrink-0"
              style={{ borderColor: '#D9D6CD', color: '#6b7069' }}
              onClick={() => {
                const base = isDirty ? dirty : JSON.parse(JSON.stringify(t));
                base.stages.push({ position: base.stages.length + 1, name: 'New stage', stage_type: 'TRANSIT', custodian_kind: 'SAME_CUSTOMER', allowed_device_types: [], required: true, no_device_policy: 'ALLOW_UNMONITORED' });
                setDirty(base);
                setSelected({ templateId: t.id, stageIdx: base.stages.length - 1 });
              }}>+ Add stage</button>
          )}
        </div>
        {isDirty && selected?.templateId === t.id && dirty.stages[selected.stageIdx] && (
          <StageSettings stage={dirty.stages[selected.stageIdx]}
            onChange={s => {
              const copy = { ...dirty, stages: [...dirty.stages] };
              copy.stages[selected.stageIdx] = { ...s, required: s.required === 'false' ? false : (s.required === 'true' ? true : s.required) };
              setDirty(copy);
            }}
            onRemove={() => {
              const copy = { ...dirty, stages: dirty.stages.filter((_, i) => i !== selected.stageIdx) };
              setDirty(copy); setSelected(null);
            }} />
        )}
      </div>
    );
  };

  return (
    <div className="p-8" data-testid="admin-journeys-page">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-4xl font-bold" style={{ color: '#1B1D1C' }}>Journey templates</h1>
        <select data-testid="journeys-customer-select" value={tenantId} onChange={e => setTenantId(e.target.value)}
          className="h-9 px-3 rounded-md border bg-white text-sm" style={{ borderColor: '#D9D6CD' }}>
          <option value="">Vertical defaults only</option>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <p className="text-sm mb-6" style={{ color: '#6b7069' }}>
        A template is the chain of stages a passport passes through. Each vertical starts from a default; each customer can copy and adjust it.</p>
      {templates.map(renderTemplate)}
      {templates.length === 0 && <p className="text-sm" style={{ color: '#9a9e96' }}>No templates yet</p>}
    </div>
  );
}
